import type { I18nContextType } from '@ecg-marktplaats/js-react-i18n';
import { TAsqPayload } from 'types/TAsq';
import {
  TAsqFormValues,
  TGetFormInitialStateConfig,
  TPartialAsqFormRecord,
  TReactiveValidator,
  TSubmitValidator,
  TSubmitValidatorParams,
  TValue,
} from './types';
import { GAAsqFieldStatus, GATradeInStatus } from '../../../../../../utils/constants';
import {
  CONTAINS_URL_REGEX,
  MAX_ASQ_LOCATION_LENGTH,
  MAX_ASQ_MESSAGE_LENGTH,
  MAX_ASQ_NAME_LENGTH,
  MIN_ASQ_MESSAGE_LENGTH,
} from '../../../../../../../../common/constants';
import { c2bCallbackKey, c2bFinancingKey, c2cCallbackKey, c2bTestDriveKey, c2cViewingKey } from './consts';
import { TTestDriveSchedulerOption } from 'types/TSellerPublicProfile';
import { testDriveSchedulerDays } from '../../consts';

const TARGET_LENGTH_SCHEDULER_VALUE = 5;

const getAsqStorageKey = (userId: string, fieldName: string) => `asq.${userId}.${fieldName}`;

export const getFieldFromLocalStorage = (userId: string, fieldName: string): string => {
  if (!localStorage) {
    return '';
  }

  const storageKey = getAsqStorageKey(userId, fieldName);
  const localStorageValue = localStorage.getItem(storageKey);
  return localStorageValue ?? '';
};

export const setFieldToLocalStorage = (userId: string, fieldName: string, value: string): void => {
  const storageKey = getAsqStorageKey(userId, fieldName);
  localStorage.setItem(storageKey, value);
};

export const getFormInitialState = ({
  userId,
  locationCity,
  phoneNumber,
  shouldHideLocation,
  shouldHideTradeIn,
  initialMessage,
}: TGetFormInitialStateConfig): TAsqFormValues => {
  const defaultBuyerName = getFieldFromLocalStorage(userId, 'buyerName');
  const defaultBuyerPhoneNumber = getFieldFromLocalStorage(userId, 'buyerPhoneNumber') || phoneNumber;
  const defaultLocation = getFieldFromLocalStorage(userId, 'location') || locationCity;
  const defaultMileage = getFieldFromLocalStorage(userId, 'mileage');
  const defaultLicensePlate = getFieldFromLocalStorage(userId, 'licensePlate');
  const defaultVehicleDescription = getFieldFromLocalStorage(userId, 'vehicleDescription');
  const defaultDamageDescription = getFieldFromLocalStorage(userId, 'damageDescription');

  let defaultImages = [];
  try {
    defaultImages = JSON.parse(getFieldFromLocalStorage(userId, 'images'));
  } catch {}

  return {
    message: initialMessage || '',
    buyerName: defaultBuyerName,
    buyerPhoneNumber: defaultBuyerPhoneNumber,
    location: !shouldHideLocation ? defaultLocation : '',
    mileage: !shouldHideTradeIn ? defaultMileage : '',
    licensePlate: !shouldHideTradeIn ? defaultLicensePlate : '',
    damageDescription: !shouldHideTradeIn ? defaultDamageDescription : '',
    vehicleDescription: !shouldHideTradeIn ? defaultVehicleDescription : '',
    images: !shouldHideTradeIn ? defaultImages : [],
  };
};

export const getInitialTradeInEnabled = (userId: string, shouldHideTradeIn: boolean): boolean => {
  if (shouldHideTradeIn) {
    return false;
  }

  return getFieldFromLocalStorage(userId, 'tradeInEnabled') === 'true';
};

const inputsLengthRules: TPartialAsqFormRecord<{ min?: number; max?: number }> = {
  message: {
    min: MIN_ASQ_MESSAGE_LENGTH,
    max: MAX_ASQ_MESSAGE_LENGTH,
  },
  buyerName: {
    max: MAX_ASQ_NAME_LENGTH,
  },
  location: {
    max: MAX_ASQ_LOCATION_LENGTH,
  },
};

const getInputLengthError = (name: string, value: string, t: I18nContextType['t']): string => {
  const lengthLimits = inputsLengthRules[name];

  if (lengthLimits?.min && value.length < lengthLimits?.min) {
    return t('form.charactersRemaining', { characters: lengthLimits?.min - value.length });
  }
  if (lengthLimits?.max && value.length > lengthLimits.max) {
    return t('form.charactersTooMany', { characters: value.length - lengthLimits.max });
  }
  return '';
};

const reactiveValidators: TPartialAsqFormRecord<TReactiveValidator> = {
  message: (value, t) => {
    const castValue = value as string;
    return getInputLengthError('message', castValue, t);
  },
  buyerName: (value, t) => {
    const castValue = value as string;
    return getInputLengthError('buyerName', castValue, t);
  },
  location: (value, t, showExtendedAsq) => {
    const castValue = value as string;
    if (!showExtendedAsq) return '';

    return getInputLengthError('location', castValue, t);
  },
};

const submitValidators: TPartialAsqFormRecord<TSubmitValidator> = {
  message: ({ value, t }: TSubmitValidatorParams) => {
    const castValue = value as string;
    if (!castValue) {
      return t('sellerContact.asq.modal.error.messageRequired');
    }
    return '';
  },
  buyerName: ({ value, t }: TSubmitValidatorParams) => {
    const castValue = (value as string).trim();
    if (!castValue) {
      return t('sellerContact.asq.modal.buyer.name.error');
    }

    if (CONTAINS_URL_REGEX.test(castValue)) {
      return t('error.invalidValue');
    }

    return '';
  },
  buyerPhoneNumber: ({ value, t, isBuyerPhoneNumberRequired }: TSubmitValidatorParams) => {
    const castValue = value as string;
    if (!castValue && isBuyerPhoneNumberRequired) {
      return t('sellerContact.asq.modal.buyer.phone.error');
    }

    return '';
  },
  mileage: ({ value, t, allowTradeInRequest, tradeInEnabled }: TSubmitValidatorParams) => {
    const castValue = value as string;
    if (!allowTradeInRequest || !tradeInEnabled) return '';

    if (!castValue) {
      return t('sellerContact.asq.modal.car.trade.mileage.error');
    }
    return '';
  },
  licensePlate: ({ value, t, allowTradeInRequest, tradeInEnabled, isMarktplaats }: TSubmitValidatorParams) => {
    const castValue = value as string;
    if (!allowTradeInRequest || !tradeInEnabled || !isMarktplaats) return '';

    if (!castValue) {
      return t('sellerContact.asq.modal.car.trade.license.error');
    }
    return '';
  },
};

export const getReactiveError = (
  name: keyof TAsqFormValues,
  value: TValue,
  t: I18nContextType['t'],
  showExtendedAsq: boolean,
): string => {
  const reactiveErrorValidator = reactiveValidators[name];
  const reactiveError = reactiveErrorValidator?.(value, t, showExtendedAsq);

  return reactiveError ?? '';
};

type TGetSubmitErrors = {
  formValues: TAsqFormValues;
  t: I18nContextType['t'];
  showExtendedAsq: boolean;
  allowTradeInRequest: boolean;
  tradeInEnabled: boolean;
  isMarktplaats: boolean;
  isBuyerPhoneNumberRequired: boolean;
};

export const getSubmitErrors = ({
  formValues,
  t,
  showExtendedAsq,
  allowTradeInRequest,
  tradeInEnabled,
  isMarktplaats,
  isBuyerPhoneNumberRequired,
}: TGetSubmitErrors): { [p: string]: string | undefined } => {
  return Object.fromEntries(
    Object.entries(formValues)
      .map(([key, value]) => {
        const name = key as keyof TAsqFormValues;
        const submitErrorValidator = submitValidators[name];
        const errorMessage =
          getReactiveError(name, value, t, showExtendedAsq) ||
          submitErrorValidator?.({
            value,
            t,
            allowTradeInRequest,
            tradeInEnabled,
            isMarktplaats,
            isBuyerPhoneNumberRequired,
          });

        return [name, errorMessage];
      })
      .filter(([, error]) => Boolean(error)),
  );
};

export const scrollToFirstError = () => {
  const firstErrorEl = document.querySelector(`#asqModal .is-errored`);
  firstErrorEl?.scrollIntoView?.({ behavior: 'smooth' });
};

export const buildSchedulerAsqPayload = (testDriveSchedulerOptions, daysWithSlotSelected) => {
  let availabilities = [];

  Object.keys(daysWithSlotSelected).forEach((day) => {
    const schedulerOptions = testDriveSchedulerOptions.find(({ dayOfWeek }) => dayOfWeek === day);
    const availabilitiesPerDay = daysWithSlotSelected[day].toSorted().map((slot) => {
      const timeSlots = schedulerOptions.timeSlots;
      return {
        dayOfWeek: day,
        startTime: timeSlots[slot].start.padStart(TARGET_LENGTH_SCHEDULER_VALUE, '0'),
        endTime: timeSlots[slot].end.padStart(TARGET_LENGTH_SCHEDULER_VALUE, '0'),
      };
    });
    availabilities = availabilities.concat(availabilitiesPerDay);
  });

  return {
    availabilities,
  };
};

export const buildSchedulerMessage = (testDriveSchedulerOptions, daysWithSlotSelected, t) => {
  const now = new Date();
  const todayIndex = now.getDay();
  const weekdaysStartingToday = [
    ...testDriveSchedulerDays.slice(todayIndex),
    ...testDriveSchedulerDays.slice(0, todayIndex),
  ];
  const selectedDaysInOrder = weekdaysStartingToday.filter((day) => Object.keys(daysWithSlotSelected).includes(day));
  let schedulerMessage = '';

  selectedDaysInOrder.forEach((day) => {
    const schedulerOptions = testDriveSchedulerOptions.find(({ dayOfWeek }) => dayOfWeek === day);
    schedulerMessage += daysWithSlotSelected[day].toSorted().reduce(
      (accumulator, currentValue, currentIndex, options) => {
        const hasNextSlot = currentIndex !== options.length - 1;
        const renderStart = options[currentIndex - 1] !== currentValue - 1;
        const renderEnd = options[currentIndex + 1] !== currentValue + 1;

        return `${accumulator}${renderStart ? `${schedulerOptions.timeSlots[currentValue].start} - ` : ''}${renderEnd ? `${schedulerOptions.timeSlots[currentValue].end}` : ''}${hasNextSlot && renderEnd ? ` ${t('testDriveScheduler.and')} ` : ''}`;
      },
      `\n· ${t(`testDriveScheduler.${day}`)} `,
    );
  });

  if (schedulerMessage.length) {
    schedulerMessage = `${t('testDriveScheduler.asqTestDrivePreferredDays')}\n${schedulerMessage}`;
  }
  return schedulerMessage;
};

export const getAsqPayload = (
  formValues: TAsqFormValues,
  tradeInEnabled: boolean,
  shouldHideTradeIn: boolean,
  shouldHideLocation: boolean,
  clickedPills: string[],
  testDriveSchedulerOptions?: TTestDriveSchedulerOption[],
  daysWithSlotSelected?: {
    [key: string]: number[];
  },
  // eslint-disable-next-line max-params
): TAsqPayload => {
  const payload: TAsqPayload = {
    message: formValues.message,
    buyerName: formValues.buyerName.trim(),
    buyerPhoneNumber: formValues.buyerPhoneNumber,
    clickedPills: clickedPills.length,
    ...(clickedPills.includes(c2bFinancingKey) && { financingInfoRequest: true }),
    ...(Boolean(clickedPills.includes(c2bCallbackKey) || clickedPills.includes(c2cCallbackKey)) && {
      callbackRequest: { availabilities: [] },
    }),
  };

  // Make sure the unnecessary information is not part of the payload to avoid data leaks
  if (!shouldHideLocation) {
    payload.location = formValues.location;
  }

  if (!shouldHideTradeIn && tradeInEnabled) {
    payload.vehicleTradeInRequest = {
      licensePlate: formValues.licensePlate.toUpperCase(),
      mileage: formValues.mileage,
      vehicleDescription: formValues.vehicleDescription,
      damageDescription: formValues.damageDescription,
      attachedMedia: (formValues.images ?? []).map((img) => ({ url: img.mediaUrl })),
    };
  }

  if (clickedPills.includes(c2bTestDriveKey) || clickedPills.includes(c2cViewingKey)) {
    payload.visitOrTestDriveRequest = buildSchedulerAsqPayload(testDriveSchedulerOptions, daysWithSlotSelected);
  }

  return payload;
};

export const getStringFieldGAStatus = (
  name: keyof TAsqFormValues,
  initialValues: TAsqFormValues,
  formValues: TAsqFormValues,
): string => {
  const wasFieldPrefilled = Boolean(initialValues[name]);
  if (formValues[name] === '') {
    return GAAsqFieldStatus.empty;
  }
  if (wasFieldPrefilled && initialValues[name] === formValues[name]) {
    return GAAsqFieldStatus.prefilled;
  }
  return GAAsqFieldStatus.filled;
};

export const getTradeInGAStatus = (isTradeInEnabled: boolean, shouldHideTradeIn: boolean): string => {
  if (shouldHideTradeIn) {
    return GATradeInStatus.unavailable;
  }

  return isTradeInEnabled ? GATradeInStatus.enabled : GATradeInStatus.disabled;
};
